import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function SSOCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, validateUser, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        
        if (!code) {
          setError('Authorization code not found');
          return;
        }

        // Send callback data to backend
        const response = await fetch(`${BACKEND_URL}/api/accounts/auth/sso-callback/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            code,
            state
          })
        });
        
        if (response.status === 409) {
          navigate('/', { replace: true });
          return;
        }
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('SSO callback failed:', response.status, errorText);
        }
        
        const data = await response.json();
        
        if (data.isAuthenticated && data.user) {
          // Hydrate auth + permissions from validation API after SSO cookie is set
          await validateUser();
          const role = data.user?.role?.toLowerCase();
          if (role === 'trainee') {
            navigate('/user/dashboard', { replace: true });
          } else {
            navigate('/admin/dashboard', { replace: true });
          }
        } else {
          throw new Error('SSO verification failed - missing user data');
        }
      } catch (err) {
        console.error('SSO callback error:', err);
        setError('Authentication failed. Please try logging in again.');
        // Redirect to home after 2 seconds to retry SSO
        setTimeout(() => navigate('/'), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && user) {
      const userRole = user.role?.toLowerCase();
      if (userRole === 'trainee') {
        navigate('/user/dashboard', { replace: true });
      } else {
        navigate('/admin/dashboard', { replace: true });
      }
      return;
    }

    handleCallback();
  }, [location.search, navigate, validateUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-font-secondary">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <p className="text-font-secondary">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return null;
}
